/**
 * ScreeningGateReport — the landing-page / questionnaire lead dashboard for one
 * of the two EARLY screening gates (Reports → Screening → Intake / Questionnaire):
 *   intake        — leads coming in via the landing-page form.
 *   questionnaire — the texted study questionnaire the lead fills out.
 *
 * Shows: summary KPIs, leads-per-day stacked by qualification status (the volume
 * trend), the lead-source mix (intake), and the question+answer that drove each
 * not-eligible / needs-review verdict — clicking a driver drills into the leads
 * behind it (an audited PHI read). The recruiter PRESCREEN gate is DQReport.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import {
  ScreeningVerdictRow, ScreeningReasonRow, ScreeningReasonContact,
  ScreeningDailyRow, ScreeningSourceRow, contactName,
} from '../../types';
import { relTime } from '../../lib';
import BarRow from './BarRow';
import { SummaryStat } from './StatBlocks';

interface Props {
  gate: 'intake' | 'questionnaire';
  from: string | null;
  to: string | null;
  studyId?: string | null;
}

// Qualification statuses in stack order (eligible at the base of each day bar).
const STATUS_ORDER: { value: string; label: string; bar: string }[] = [
  { value: 'eligible', label: 'Eligible', bar: 'bg-green-500' },
  { value: 'needs_review', label: 'Needs review', bar: 'bg-amber-500' },
  { value: 'not_eligible', label: 'Not eligible', bar: 'bg-red-500' },
  { value: 'pending', label: 'Incomplete', bar: 'bg-gray-400' },     // intake's incomplete value
  { value: 'incomplete', label: 'Incomplete', bar: 'bg-gray-400' },  // questionnaire's
  { value: 'unknown', label: 'Unknown', bar: 'bg-gray-300' },
];

/** "Label = Yes" when there's a real answer, else just the question label. */
function driverLabel(r: ScreeningReasonRow): string {
  return r.answer_value && r.answer_value !== '(no answer)' ? `${r.label} = ${r.answer_value}` : r.label;
}

/** 'YYYY-MM-DD' → 'Jun 1' (parsed as a local calendar day). */
function dayShort(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ScreeningGateReport({ gate, from, to, studyId = null }: Props) {
  const [verdicts, setVerdicts] = useState<ScreeningVerdictRow[]>([]);
  const [reasons, setReasons] = useState<ScreeningReasonRow[]>([]);
  const [daily, setDaily] = useState<ScreeningDailyRow[]>([]);
  const [sources, setSources] = useState<ScreeningSourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down: the selected driver + its leads. answerValue null = the whole
  // question (a collapsed multi-answer driver, e.g. BMI); a string = one answer.
  const [drill, setDrill] = useState<{ questionKey: string; kind: string; answerValue: string | null; label: string } | null>(null);
  const [drillRows, setDrillRows] = useState<ScreeningReasonContact[] | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setDrill(null);
    setDrillRows(null);
    const params = { from: from || undefined, to: to || undefined, study_id: studyId || undefined };
    Promise.all([
      api.get(`/api/crm/analytics/screening/${gate}/verdicts`, { params }),
      api.get(`/api/crm/analytics/screening/${gate}/reasons`, { params }),
      api.get(`/api/crm/analytics/screening/${gate}/daily`, { params }),
      gate === 'intake'
        ? api.get(`/api/crm/analytics/screening/${gate}/sources`, { params })
        : Promise.resolve({ data: { rows: [] } }),
    ])
      .then(([v, r, d, s]) => {
        if (!alive) return;
        setVerdicts(v.data?.rows || []);
        setReasons(r.data?.rows || []);
        setDaily(d.data?.rows || []);
        setSources(s.data?.rows || []);
        setError(null);
      })
      .catch((err) => { if (alive) setError(err?.response?.data?.error || 'Failed to load screening'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [gate, from, to, studyId]);

  // Drill fetch for the selected driver (matches the clicked answer exactly).
  useEffect(() => {
    if (!drill) return;
    let alive = true;
    setDrillRows(null);
    api.get(`/api/crm/analytics/screening/${gate}/reason-contacts`, {
      params: {
        question_key: drill.questionKey, kind: drill.kind,
        // null = all answers for the question (a collapsed driver); omit the param.
        answer_value: drill.answerValue ?? undefined,
        from: from || undefined, to: to || undefined, study_id: studyId || undefined, limit: 50,
      },
    })
      .then((res) => { if (alive) setDrillRows(res.data?.contacts || []); })
      .catch(() => { if (alive) setDrillRows([]); });
    return () => { alive = false; };
  }, [drill, gate, from, to, studyId]);

  const byVerdict = useMemo(() => new Map(verdicts.map((v) => [v.verdict, Number(v.total)])), [verdicts]);
  const total = useMemo(() => verdicts.reduce((s, v) => s + Number(v.total), 0), [verdicts]);
  const incomplete = (byVerdict.get('pending') || 0) + (byVerdict.get('incomplete') || 0);

  // Leads per day, pivoted into { day, total, byStatus } and sorted ascending.
  const daysData = useMemo(() => {
    const byDay = new Map<string, { day: string; total: number; byStatus: Record<string, number> }>();
    for (const r of daily) {
      const d = byDay.get(r.day) || { day: r.day, total: 0, byStatus: {} };
      d.byStatus[r.verdict] = (d.byStatus[r.verdict] || 0) + Number(r.total);
      d.total += Number(r.total);
      byDay.set(r.day, d);
    }
    return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
  }, [daily]);
  const maxDayTotal = useMemo(() => Math.max(1, ...daysData.map((d) => d.total)), [daysData]);
  const avgPerDay = daysData.length ? Math.round(total / daysData.length) : 0;
  const presentStatuses = useMemo(
    () => STATUS_ORDER.filter((s) => daysData.some((d) => d.byStatus[s.value]))
      // collapse the two "Incomplete" values into one legend chip
      .filter((s, i, arr) => arr.findIndex((x) => x.label === s.label) === i),
    [daysData],
  );

  // Lead source mix (intake): { source, total, eligible } sorted by volume.
  const sourceData = useMemo(() => {
    const bySource = new Map<string, { source: string; total: number; eligible: number }>();
    for (const r of sources) {
      const s = bySource.get(r.lead_source) || { source: r.lead_source, total: 0, eligible: 0 };
      s.total += Number(r.total);
      if (r.verdict === 'eligible') s.eligible += Number(r.total);
      bySource.set(r.lead_source, s);
    }
    return [...bySource.values()].sort((a, b) => b.total - a.total);
  }, [sources]);
  const maxSource = useMemo(() => Math.max(1, ...sourceData.map((s) => s.total)), [sourceData]);

  const excludeDrivers = useMemo(() => reasons.filter((r) => r.kind === 'exclude'), [reasons]);
  const reviewDrivers = useMemo(() => reasons.filter((r) => r.kind === 'review'), [reasons]);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading screening…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (total === 0) {
    return (
      <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
        No {gate === 'intake' ? 'leads have come in' : 'questionnaires have been completed'} in this range yet.
      </p>
    );
  }

  const pct = (n: number) => (total > 0 ? `${Math.round((n / total) * 100)}%` : '—');

  const DriverSection = ({ title, rows, kind }: { title: string; rows: ScreeningReasonRow[]; kind: string }) => {
    if (rows.length === 0) return null;
    // Collapse to ONE line per question. A question with multiple distinct answers
    // (BMI, age, any numeric) becomes a single "Label → N" bar; a single-answer
    // question (Yes/No) keeps its informative "Label = value". The drill then
    // targets that one answer, or — when collapsed — every answer for the question.
    const byQuestion = new Map<string, { questionKey: string; label: string; total: number; answers: ScreeningReasonRow[] }>();
    for (const r of rows) {
      const g = byQuestion.get(r.question_key) || { questionKey: r.question_key, label: r.label, total: 0, answers: [] };
      g.total += Number(r.total);
      g.answers.push(r);
      byQuestion.set(r.question_key, g);
    }
    const groups = [...byQuestion.values()].sort((a, b) => b.total - a.total);
    const max = Math.max(1, ...groups.map((g) => g.total));
    return (
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h4>
        <div className="space-y-0.5">
          {groups.map((g) => {
            const single = g.answers.length === 1 ? g.answers[0] : null;
            const answerValue = single ? single.answer_value : null; // null = all answers for the question
            const label = single ? driverLabel(single) : g.label;
            const active = drill?.questionKey === g.questionKey && drill?.kind === kind && drill?.answerValue === answerValue;
            return (
              <BarRow
                key={`${kind}:${g.questionKey}`}
                label={label}
                count={g.total}
                max={max}
                color={kind === 'exclude' ? 'bg-red-400' : 'bg-amber-400'}
                active={active}
                onClick={() => setDrill((d) =>
                  d?.questionKey === g.questionKey && d?.kind === kind && d?.answerValue === answerValue
                    ? null
                    : { questionKey: g.questionKey, kind, answerValue, label })}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <SummaryStat label={gate === 'intake' ? 'Leads in' : 'Questionnaires'} value={total.toLocaleString()} sub={`${avgPerDay}/day avg`} />
        <SummaryStat label="Eligible" value={(byVerdict.get('eligible') || 0).toLocaleString()} sub={pct(byVerdict.get('eligible') || 0)} highlight={(byVerdict.get('eligible') || 0) > 0} />
        <SummaryStat label="Needs review" value={(byVerdict.get('needs_review') || 0).toLocaleString()} sub={pct(byVerdict.get('needs_review') || 0)} />
        <SummaryStat label="Not eligible" value={(byVerdict.get('not_eligible') || 0).toLocaleString()} sub={pct(byVerdict.get('not_eligible') || 0)} />
        <SummaryStat label="Incomplete" value={incomplete.toLocaleString()} sub={pct(incomplete)} />
        <SummaryStat label="Days" value={daysData.length.toLocaleString()} sub="with leads" />
      </div>

      {/* Leads per day, stacked by qualification status */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Leads per day by status</h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {presentStatuses.map((s) => (
              <span key={s.label} className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                <span className={`inline-block h-2 w-2 rounded-sm ${s.bar}`} /> {s.label}
              </span>
            ))}
          </div>
        </div>
        {daysData.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No daily data in this range.</p>
        ) : (
          <div className="overflow-x-auto pb-1">
            <div className="flex items-end gap-1" style={{ minHeight: 150 }}>
              {daysData.map((d) => (
                <div key={d.day} className="flex flex-shrink-0 flex-col items-center gap-1" title={`${dayShort(d.day)}: ${d.total} lead${d.total === 1 ? '' : 's'}`}>
                  <div className="flex w-5 flex-col justify-end" style={{ height: 124 }}>
                    <div className="flex w-full flex-col-reverse overflow-hidden rounded-t-sm" style={{ height: `${(d.total / maxDayTotal) * 100}%` }}>
                      {STATUS_ORDER.filter((s) => d.byStatus[s.value]).map((s) => (
                        <div
                          key={s.value}
                          className={s.bar}
                          style={{ height: `${(d.byStatus[s.value] / d.total) * 100}%` }}
                          title={`${s.label}: ${d.byStatus[s.value]}`}
                        />
                      ))}
                    </div>
                  </div>
                  <span className="text-[10px] font-medium leading-none tabular-nums text-gray-600 dark:text-gray-300">{d.total}</span>
                  <span className="whitespace-nowrap text-[9px] leading-none text-gray-400">{dayShort(d.day)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lead source mix (intake only) */}
      {gate === 'intake' && sourceData.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">By lead source</h3>
          <div className="space-y-0.5">
            {sourceData.map((s) => (
              <BarRow
                key={s.source}
                label={s.source}
                count={s.total}
                max={maxSource}
                hint={`${s.eligible.toLocaleString()} eligible`}
                color="bg-primary-500"
              />
            ))}
          </div>
        </div>
      )}

      {/* Drivers + drill-down */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          {excludeDrivers.length === 0 && reviewDrivers.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No disqualifying or flagged answers recorded yet.</p>
          ) : (
            <>
              <DriverSection title="Not eligible — top drivers" rows={excludeDrivers} kind="exclude" />
              <DriverSection title="Needs review — top drivers" rows={reviewDrivers} kind="review" />
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          {!drill ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Click a driver to see the leads behind it.</p>
          ) : (
            <>
              <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{drill.label}</h3>
              {drillRows === null ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : drillRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No leads.</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {drillRows.map((d) => (
                    <li key={`${d.contact_id}-${d.occurred_at}`} className="flex items-center justify-between gap-2 py-2">
                      {d.contact ? (
                        <Link
                          to={`/crm/contacts/${d.contact.id}`}
                          className="min-w-0 truncate text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
                        >
                          {contactName(d.contact)}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-500">(contact)</span>
                      )}
                      <span className="flex flex-shrink-0 items-center gap-2">
                        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {d.answer_value}
                        </span>
                        <span className="text-xs text-gray-400">{relTime(d.occurred_at)} ago</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
